import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import moment from 'moment';
import isEqual from 'lodash/isEqual';
import {AppDataGrid} from 'components/common/ag-grid/AppDataGrid.component';
import {ConfigurationPage} from 'components/common/configuration/ConfigurationPage.component';
import {NewCalendar} from 'components/common/calendar/Calendar.component';
import type {DateRange} from 'components/common/calendar/CalendarQuickSelect.component';
import {
    CARD_BOTTOM_GAP,
    configurationGridHeight
} from 'components/common/ag-grid/configuration/appConfigurationGrid.function';
import {useAvailableHeight} from 'components/common/ag-grid/appGrid.function';
import {useTranslation} from 'components/translations/useTranslation';
import {ORGANISATION_CHART} from 'components/resizableTable/columnsMap';
import type {ColumnMapEntry, DataGridFilters, DataGridPageRequest, DataGridSortingLogic, DataGridTasks} from 'types/appDataGrid';
import type {OrganisationChartNode} from 'types/modals';
import type {NewOrganisationChartProps, OrgChartNodeList} from 'types/organisationChart';
import * as reduxSelectors from '../store/application.reducers';
import OrganisationChart from './OrganisationChart.component';
import {isNewOrganisationChartEnabled} from './organisationChart.function';
import {OrgChartTree} from './OrgChartTree.component';
import {
    changeSelectedDate,
    changeSelectedNode,
    getOrganizationChartUsers,
    getOrgChartTreeNodes,
    loadMoreUsers,
    orgChartFilterChanged,
    sortingColumnChanged
} from './organisationChart.action';
import './newOrganisationChart.scss';

// the page, as its class names and ids spell it
const PAGE = 'organisation-chart';

// the tree and the grid stand side by side and fill the page, keeping the card's own padding and
// the page's bottom margin below them
const GRID_HEIGHT = configurationGridHeight(PAGE);

// The tree panel stands beside the grid at the grid's height, so it is allowed to shrink as far as
// the grid is (see AppDataGrid, which measures itself with no floor).
const TREE_HEIGHT = Object.freeze({...GRID_HEIGHT, minHeight: 0});

const DATE_INPUT_ID = 'organisation-chart-inspect-date';

// what the store keeps the inspect date as
const ISO_DAY = 'YYYY-MM-DD';

// The classic column map names its headings with plain words; these are the translation keys
// for them. The map stays the classic table's, so the widths, the filters and what the backend
// sorts on are one and the same in both renderings.
const COLUMN_CAPTIONS: Record<string, string> = {
    roleName: 'organisation.role',
    holder: 'organisation.holder',
    deputy: 'organisation.deputy'
};
// The classic table's 80px Deputy column is too narrow for the grid's filter dropdown, which
// clips its picked word beside the chevron; the classic table itself keeps its own width.
const COLUMN_WIDTHS: Record<string, number> = {
    deputy: 120
};
const COLUMNS: ColumnMapEntry[] = (ORGANISATION_CHART as ColumnMapEntry[]).map(entry => ({
    ...entry,
    caption: COLUMN_CAPTIONS[entry.value] ?? entry.caption,
    width: COLUMN_WIDTHS[entry.value] ?? entry.width
}));

// Before a unit is picked there is no list to be empty - the grid draws nothing over its rows
// rather than the "no approvers" it reads once a unit turns out to have none.
const NOTHING_PICKED = <span/>;

/**
 * ag-grid rendering of the Configuration -> Organisation chart page, behind
 * fe-use-new-organisation-chart. The classic page it replaces is in OrganisationChart.component.
 *
 * The tree of units is on the left, and the approvers of the unit picked in it on the right - in
 * the column-map grid the overview pages use, since this list too is filtered, sorted and paged
 * by the backend. Compact, it is the tree alone, which is how the organisation chart popup picks
 * a unit.
 */
export const NewOrganisationChart = ({isCompact = false, onChangeSelectedNode}: NewOrganisationChartProps) => {

    const {translate} = useTranslation();
    const dispatch = useDispatch();

    const currentContextId = useSelector(reduxSelectors.getUsersCurrentContextId);
    const orgChartTree: OrgChartNodeList | null = useSelector(reduxSelectors.getOrgChartTree);
    const selectedNode: OrganisationChartNode | null = useSelector(reduxSelectors.getOrgChartSelectedNode);
    const selectedDate: string = useSelector(reduxSelectors.getOrgChartSelectedDate);
    const users: DataGridTasks = useSelector(reduxSelectors.getOrgChartUsers);
    const filters: DataGridFilters = useSelector(reduxSelectors.getOrgChartFilters);
    const sortingLogic: DataGridSortingLogic = useSelector(reduxSelectors.getOrgChartSortingLogic);
    const loadingStatus: number | undefined = useSelector(reduxSelectors.getOrgChartUsersLoadingStatus);
    // mapped afresh on every read, so compared by value: the same roles must not redraw the filter row
    const roles: unknown[] = useSelector(reduxSelectors.getOrgChartRoles, isEqual);

    // the tree and the grid stand in one row, so the tree panel is measured from the same top the
    // grid measures itself from. The compact tree never mounts that panel, so it is not measured
    const [treeElement, setTreeElement] = useState<HTMLDivElement | null>(null);
    const treeHeight = useAvailableHeight(treeElement, TREE_HEIGHT);

    // the Role filter's options, handed over as one object for as long as the roles stay the same
    const filterSelectors = useMemo(() => ({role_select: roles}), [roles]);

    /**
     * How many times a unit has been picked. Every pick starts the list over - no filter and no
     * sort, the same unit picked again included - and the grid is remounted on it: the store
     * drops the sort, but ag-grid would keep the arrow on the heading (see buildColumnDefs, which
     * leaves out a sort there is none of rather than clearing it).
     */
    const [selectionCount, setSelectionCount] = useState(0);

    // The day the store holds, read as a local day so the calendar shows that day wherever the
    // user is. The calendar reads it on mount only, which is why it is keyed on the day below.
    const pickerDate = useMemo(() => moment(selectedDate, ISO_DAY).toDate(), [selectedDate]);

    // The tree is loaded for the date the store holds, on mount and again when the company
    // changes, as the wrapper the classic page extended did. A new date reloads it through its
    // own action (see changeSelectedDate), so the date is read off a ref rather than depended on.
    const dateRef = useRef(selectedDate);
    useEffect(() => {
        dateRef.current = selectedDate;
    }, [selectedDate]);

    useEffect(() => {
        dispatch(getOrgChartTreeNodes(dateRef.current, null, currentContextId));
    }, [dispatch, currentContextId]);

    const changeDate = useCallback(({startDate}: DateRange) => {
        // a field typed empty is no date to inspect the chart as of, and the day already shown is
        // no new one
        const day = startDate ? moment(startDate).format(ISO_DAY) : null;
        if (day && day !== selectedDate) {
            dispatch(changeSelectedDate(day, currentContextId));
        }
    }, [dispatch, selectedDate, currentContextId]);

    const loadChildren = useCallback((nodeId: string | number) => {
        dispatch(getOrgChartTreeNodes(selectedDate, nodeId, currentContextId));
    }, [dispatch, selectedDate, currentContextId]);

    const selectNode = useCallback((node: OrganisationChartNode) => {
        dispatch(changeSelectedNode(node));
        onChangeSelectedNode?.(node);
        // the compact tree has no grid to show the unit's approvers in
        if (!isCompact) {
            setSelectionCount(count => count + 1);
            dispatch(getOrganizationChartUsers(selectedDate, node, null, currentContextId));
        }
    }, [dispatch, onChangeSelectedNode, isCompact, selectedDate, currentContextId]);

    const filterChanged = useCallback((newFilters: DataGridFilters) => {
        dispatch(orgChartFilterChanged(newFilters));
    }, [dispatch]);

    // a filter set, or a sort picked, before any unit is picked has nothing to list yet - the
    // action reads the unit's id, and there is none
    const refreshGrid = useCallback((newFilters: DataGridFilters) => {
        if (selectedNode) {
            dispatch(getOrganizationChartUsers(selectedDate, selectedNode, newFilters, currentContextId));
        }
    }, [dispatch, selectedDate, selectedNode, currentContextId]);

    const sortChanged = useCallback((newFilters: DataGridFilters) => {
        dispatch(sortingColumnChanged(newFilters));
    }, [dispatch]);

    const loadMore = useCallback((request: DataGridPageRequest) => {
        if (selectedNode) {
            dispatch(loadMoreUsers(request, selectedDate, selectedNode, currentContextId));
        }
    }, [dispatch, selectedDate, selectedNode, currentContextId]);

    const hasTree = !!orgChartTree?.rows?.length;

    const datePicker = (
        <div className="d-flex align-items-center gap-3">
            <label htmlFor={DATE_INPUT_ID} className="mb-0">{translate('organisation.inspectDate')}</label>
            <div className="new-organisation-chart__date" data-cy="organisationChart.date">
                <NewCalendar key={selectedDate}
                             id={DATE_INPUT_ID}
                             singlePanel
                             translate={translate}
                             initialStartDate={pickerDate}
                             initialEndDate={pickerDate}
                             onChange={changeDate}/>
            </div>
        </div>
    );

    const tree = hasTree &&
        <OrgChartTree rows={orgChartTree.rows}
                      selectedNode={selectedNode}
                      label={translate('organisation.header')}
                      loadChildren={loadChildren}
                      onSelect={selectNode}/>;

    if (isCompact) {
        return (
            <div className="new-organisation-chart new-organisation-chart--compact">
                <div className="mb-4">{datePicker}</div>
                {hasTree
                    ? <div className="new-organisation-chart__tree-panel">{tree}</div>
                    : <span>{translate('generic.noData')}</span>}
            </div>
        );
    }

    return (
        <ConfigurationPage page={PAGE}
                           titleKey="organisation.header"
                           infoKey="organisation.headerInfo"
                           translate={translate}>

            {/* the date stays at hand with no tree to show, since another date may well have one */}
            <div className="d-flex justify-content-end align-items-center mb-24">
                {datePicker}
            </div>

            {hasTree
                ? <div className="new-organisation-chart__body">
                    {/* a measured 0 falls back to the stylesheet's height on purpose: AppGrid does
                        the same with the grid's own 0, so the two panels stay one height */}
                    <div className="new-organisation-chart__tree-panel"
                         ref={setTreeElement}
                         style={treeHeight ? {height: treeHeight} : undefined}>
                        {tree}
                    </div>

                    <div className="new-organisation-chart__users">
                        <AppDataGrid
                            // a pick starts the grid over (see selectionCount), and so does a new
                            // date, which leaves no unit picked
                            key={selectedNode ? `${selectedNode.id}-${selectionCount}` : 'none'}
                            className="new-organisation-chart__grid"
                            dataCy="organisationChart.grid"
                            tasks={users}
                            defaultColumns={COLUMNS}
                            filters={filters}
                            translate={translate}
                            selectors={filterSelectors}
                            loadingStatus={loadingStatus}
                            hideRowsNumber={true}
                            bottomGap={CARD_BOTTOM_GAP}
                            watchSelector={GRID_HEIGHT.watchSelector}
                            sortingLogic={sortingLogic}
                            filterChanged={filterChanged}
                            refreshGrid={refreshGrid}
                            sortingColumnChanged={sortChanged}
                            loadMoreTasks={loadMore}
                            noDataString={selectedNode ? translate('organisation.noOrganisationData') : NOTHING_PICKED}
                        />
                    </div>
                </div>
                : <span>{translate('generic.noData')}</span>
            }
        </ConfigurationPage>
    );
};

/**
 * Configuration -> Organisation chart, and the tree the organisation chart popup picks a unit
 * from. The flag is read here rather than around the route, so the page itself decides which of
 * its two renderings it is - wherever it is rendered.
 */
export const OrganisationChartWrapper = (props: NewOrganisationChartProps) =>
    isNewOrganisationChartEnabled() ? <NewOrganisationChart {...props}/> : <OrganisationChart {...props}/>;

export default OrganisationChartWrapper;
